import React, { useState } from "react";
import {Bot,CheckCircle2,ChevronDown,Clock3,Pause,Play,RefreshCw,RotateCcw,UserRound,XCircle,Zap} from "lucide-react";

function RuntimeHistory({
  history = [],
  rawHistory = [],
  loading = false,
  rawLoading = false,
}) {
  const [activeTab, setActiveTab] = useState("activity");
  const [expanded, setExpanded] = useState(false);

  const activityRecords = Array.isArray(history) ? history : [];
  const rawRecords = Array.isArray(rawHistory) ? rawHistory : [];

  const records = activeTab === "activity" ? activityRecords : rawRecords;
  const isLoading = activeTab === "activity" ? loading : rawLoading;
  const visibleRecords = expanded ? records : records.slice(0, 4);

  const getEventConfig = (event) => {
    const type = String(event?.event_type ||  event?.event ||  event?.type ||  event?.action ||  "").toUpperCase();

    if (type.includes("BOT_ASSIGNED") || type.includes("BOT_CHANGED")) {
      return {
        icon: Bot,
        label: "Bot Assigned",
        iconClass: "text-[#3d9bd2] dark:text-[#63b9e8]",
        bgClass: "bg-[#eaf6fd] dark:bg-[#123447]",
        borderClass: "border-[#d8ebf5] dark:border-[#1d4b62]",
      };
    }

    if (type.includes("USER_ASSIGNED") || type.includes("ASSIGN_USER")) {
      return {
        icon: UserRound,
        label: "User Assigned",
        iconClass: "text-[#398fc7] dark:text-[#63b9e8]",
        bgClass: "bg-[#edf8fd] dark:bg-[#123447]",
        borderClass: "border-[#d8ebf5] dark:border-[#1d4b62]",
      };
    }

    if (type.includes("MODE_CHANGED") || type.includes("MODE_CHANGE")) {
      return {
        icon: Zap,
        label: "Mode Changed",
        iconClass: "text-[#3d9bd2] dark:text-[#6cc2ed]",
        bgClass: "bg-[#eef8fd] dark:bg-[#123447]",
        borderClass: "border-[#dcecf5] dark:border-[#1d4b62]",
      };
    }

    if (type.includes("PAUSED")) {
      return {
        icon: Pause,
        label: "Conversation Paused",
        iconClass: "text-[#4d9bc5] dark:text-[#67b8df]",
        bgClass: "bg-[#eef7fb] dark:bg-[#153748]",
        borderClass: "border-[#dcecf5] dark:border-[#1d4b62]",
      };
    }

    if (type.includes("RESUMED")) {
      return {
        icon: Play,
        label: "Conversation Resumed",
        iconClass: "text-[#3d9bd2] dark:text-[#63b9e8]",
        bgClass: "bg-[#eaf6fd] dark:bg-[#123447]",
        borderClass: "border-[#d8ebf5] dark:border-[#1d4b62]",
      };
    }

    if (type.includes("RESTART") || type.includes("RESTARTED")) {
      return {
        icon: RefreshCw,
        label: "Conversation Restarted",
        iconClass: "text-[#398fc7] dark:text-[#63b9e8]",
        bgClass: "bg-[#edf8fd] dark:bg-[#123447]",
        borderClass: "border-[#d8ebf5] dark:border-[#1d4b62]",
      };
    }

    if (type.includes("RESET") || type.includes("RESETTED")) {
      return {
        icon: RotateCcw,
        label: "Runtime Reset",
        iconClass: "text-[#4d9bc5] dark:text-[#67b8df]",
        bgClass: "bg-[#eef7fb] dark:bg-[#153748]",
        borderClass: "border-[#dcecf5] dark:border-[#1d4b62]",
      };
    }

    if (type.includes("CLOSED") || type.includes("CLOSE")) {
      return {
        icon: XCircle,
        label: "Conversation Closed",
        iconClass: "text-[#7c9aaa] dark:text-[#8baebf]",
        bgClass: "bg-[#f1f6f9] dark:bg-[#172a35]",
        borderClass: "border-[#e0e9ee] dark:border-[#293f4c]",
      };
    }

    return {
      icon: CheckCircle2,
      label: event?.event || "Runtime Updated",
      iconClass: "text-[#6d8b9b] dark:text-[#8baebf]",
      bgClass: "bg-[#f1f6f9] dark:bg-[#172a35]",
      borderClass: "border-[#e0e9ee] dark:border-[#293f4c]",
    };
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getDescription = (event, config) => {
    const type = String(event?.event_type ||event?.event ||event?.type ||event?.action ||"").toUpperCase();
    const metadata =event?.metadata && !Array.isArray(event.metadata)? event.metadata: {};
 
    if (type.includes("BOT_ASSIGNED") || type.includes("BOT_CHANGED")) {
      const botName = metadata?.bot_name || event?.bot?.name || event?.whatsapp_bot?.name;
      return botName ? `${botName} assigned to conversation` : "Automation bot assigned";
    }

    if (type.includes("USER_ASSIGNED") || type.includes("ASSIGN_USER")) {
      const userName = metadata?.user_name || metadata?.assigned_user_name || event?.assigned_user?.name || event?.user?.name;
      return userName ? `${userName} assigned to conversation` : "Team member assigned";
    }

    if (type.includes("MODE_CHANGED") || type.includes("MODE_CHANGE")) {
      const oldMode = metadata?.old_mode || event?.old_mode;
      const newMode = metadata?.new_mode || event?.new_mode;

      if (oldMode && newMode) return `${oldMode} → ${newMode}`;
      
      return newMode ? `Conversation switched to ${newMode} mode` : "Conversation mode was changed";
    }

    if (type.includes("PAUSED")) {
      const pausedUntil = metadata?.paused_until;
      return pausedUntil ? `Paused until ${pausedUntil}` : "Conversation was paused";
    }

    if (type.includes("RESUMED")) {
      return "Conversation resumed successfully";
    }

    if (type.includes("RESTART") || type.includes("RESTARTED")) {
      return "Conversation runtime was restarted";
    }

    if (type.includes("CLOSED") || type.includes("CLOSE")) {
      return "Conversation was closed";
    }

    if (type.includes("RESET") || type.includes("RESETTED")) {
      return "Conversation runtime was reset";
    }

    return event?.description || event?.message || event?.notes || config.label;
  };

  if (!isLoading && records.length === 0) {
    return (
      <div className="overflow-hidden rounded-md border border-[#e3edf3] bg-white shadow-[0_1px_3px_rgba(20,90,125,0.03)] dark:border-[#1f3440] dark:bg-[#111e26]">
        <div className="border-b border-[#edf3f6] px-3 pt-3 dark:border-[#1d303b]">
          <div className="flex items-center gap-2">
            <div className="relative flex h-7 w-7 items-center justify-center overflow-hidden rounded-lg border border-[#dcecf5] bg-[#eaf6fd] text-[#3d9bd2] dark:border-[#1d4b62] dark:bg-[#123447] dark:text-[#63b9e8]">
              <Clock3 size={13} strokeWidth={1.9} />
            </div>

            <div>
              <p className="text-[12px] font-semibold text-[#526675] dark:text-[#d5e2e8]">
                Conversation History
              </p>

              <p className="text-[10px] text-[#9aa8b3] dark:text-[#718995]">
                Track conversation activity
              </p>
            </div>
          </div>

          <div className="mt-2.5 flex gap-1 rounded-lg border border-[#e8f0f4] bg-[#f7fafc] p-1 dark:border-[#1d303b] dark:bg-[#0d1a21]">
            <TabButton
              active={activeTab === "activity"}
              onClick={() => {
                setActiveTab("activity");
                setExpanded(false);
              }}
              label="Activity"
            />

            <TabButton
              active={activeTab === "runtime"}
              onClick={() => {
                setActiveTab("runtime");
                setExpanded(false);
              }}
              label="Runtime Events"
            />
          </div>
        </div>

        <div className="px-3 py-6 text-center">
          <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl border border-[#dcecf5] bg-[#eaf6fd] text-[#3d9bd2]">
            <Clock3 size={15} strokeWidth={1.8} />
          </div>

          <p className="mt-2 text-[11px] font-semibold text-[#526675] dark:text-[#d5e2e8]">
            No {activeTab === "activity" ? "activity" : "runtime events"}
          </p>

          <p className="mt-0.5 text-[9px] text-[#9aa8b3] dark:text-[#718995]">
            Conversation activity will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-[#e3edf3] bg-white shadow-[0_1px_3px_rgba(20,90,125,0.03)] dark:border-[#1f3440] dark:bg-[#111e26]">
      <div className="border-b border-[#edf3f6] px-3 pt-3 dark:border-[#1d303b]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative flex h-7 w-7 items-center justify-center overflow-hidden rounded-lg border border-[#dcecf5] bg-[#eaf6fd] text-[#3d9bd2] dark:border-[#1d4b62] dark:bg-[#123447] dark:text-[#63b9e8]">
              <Clock3 size={13} strokeWidth={1.9} />
            </div>

            <div>
              <p className="text-[12px] font-semibold text-[#526675] dark:text-[#d5e2e8]">
                Conversation History
              </p>

              <p className="text-[10px] text-[#9aa8b3] dark:text-[#718995]">
                Track conversation activity
              </p>
            </div>
          </div>

          <span className="rounded-full border border-[#dcecf5] bg-[#f5fafc] px-2 py-1 text-[9px] font-semibold text-[#8c9da8] dark:border-[#244250] dark:bg-[#172b35] dark:text-[#7895a3]">
            {records.length}
          </span>
        </div>

        <div className="mt-2.5 flex gap-1 rounded-lg border border-[#e8f0f4] bg-[#f7fafc] p-1 dark:border-[#1d303b] dark:bg-[#0d1a21]">
          <TabButton
            active={activeTab === "activity"}
            onClick={() => {
              setActiveTab("activity");
              setExpanded(false);
            }}
            label="Activity"
          />

          <TabButton
            active={activeTab === "runtime"}
            onClick={() => {
              setActiveTab("runtime");
              setExpanded(false);
            }}
            label="Runtime Events"
          />
        </div>
      </div>

      <div className="px-3 py-3">
        {isLoading ? (
          <LoadingState />
        ) : (
          <div className="relative">
            {visibleRecords.length > 1 && (
              <div className="absolute bottom-3 left-[13px] top-3 w-px bg-[#e5eef3] dark:bg-[#243a46]" />
            )}

            <div className="space-y-3.5">
              {visibleRecords.map((event, index) => {
                const config = getEventConfig(event);
                const Icon = config.icon;

                const timestamp = event?.created_at || event?.updated_at || event?.timestamp || event?.occurred_at;

                return (
                  <div
                    key={event?.id || `${index}-${timestamp}`}
                    className="relative flex gap-2.5"
                  >
                    <div
                      className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border ${config.borderClass} ${config.bgClass} ${config.iconClass}`}
                    >
                      <Icon size={13} strokeWidth={1.9} />
                    </div>

                    <div className="min-w-0 flex-1 pt-0.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="truncate text-[11px] font-semibold text-[#526675] dark:text-[#d2e0e6]">
                          {config.label}
                        </p>

                        <span className="shrink-0 text-[9px] text-[#9aa8b3] dark:text-[#6f8995]">
                          {formatDate(timestamp)}
                        </span>
                      </div>

                      <p className="mt-0.5 line-clamp-2 text-[10px] leading-4 text-[#8b9ca7] dark:text-[#829aa6]">
                        {getDescription(event, config)}
                      </p>

                      {activeTab === "runtime" && (
                        <RuntimeDetails event={event} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!isLoading && records.length > 4 && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-[#e3edf3] bg-[#f8fbfd] px-2 py-2 text-[10px] font-semibold text-[#718493] transition-all duration-200 hover:border-[#cfe5f1] hover:bg-[#edf8fd] hover:text-[#398fc7] dark:border-[#243a46] dark:bg-[#14252e] dark:text-[#829aa6]"
          >
            {expanded ? "Show Less" : `View ${records.length - 4} More`}

            <ChevronDown
              size={11}
              className={
                expanded
                  ? "rotate-180 text-[#3d9bd2] transition-transform"
                  : "transition-transform"
              }
            />
          </button>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-md px-2 py-2 text-[10px] font-semibold transition-all duration-200 ${
        active
          ? "bg-white text-[#3d9bd2] shadow-[0_1px_4px_rgba(61,155,210,0.10)] dark:bg-[#172b35] dark:text-[#63b9e8]"
          : "text-[#9aa8b3] hover:bg-white/70 hover:text-[#607582] dark:text-[#718995]"
      }`}
    >
      {label}
    </button>
  );
}

function RuntimeDetails({ event }) {
  const metadata =
    event?.metadata && !Array.isArray(event.metadata) ? event.metadata : {};
  const type = String(event?.event_type || "").toUpperCase();
  const details = [];

  if (type === "MODE_CHANGED") {
    if (metadata?.old_mode) details.push(`From: ${metadata.old_mode}`);
    if (metadata?.new_mode) details.push(`To: ${metadata.new_mode}`);
  }

  if (type === "PAUSED" && metadata?.paused_until) {
    details.push(`Until: ${metadata.paused_until}`);
  }

  if (type === "BOT_ASSIGNED" && metadata?.bot_name) {
    details.push(`Bot: ${metadata.bot_name}`);
  }

  if (event?.from_stage?.name) {
    details.push(`Stage: ${event.from_stage.name}`);
  }

  if (event?.status) {
    details.push(`Status: ${event.status}`);
  }

  if (details.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {details.map((detail, index) => (
        <span
          key={`${detail}-${index}`}
          className="rounded-md border border-[#e3edf3] bg-[#f7fafc] px-1.5 py-1 text-[9px] font-medium text-[#8395a0] dark:border-[#263f4b] dark:bg-[#162a34] dark:text-[#7894a1]"
        >
          {detail}
        </span>
      ))}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((item) => (
        <div key={item} className="flex items-center gap-2.5">
          <div className="h-7 w-7 animate-pulse rounded-lg bg-[#eef3f6] dark:bg-[#1a2d36]" />

          <div className="flex-1">
            <div className="h-3 w-24 animate-pulse rounded bg-[#edf3f6] dark:bg-[#1a2d36]" />

            <div className="mt-1.5 h-2.5 w-36 animate-pulse rounded bg-[#f5f8fa] dark:bg-[#16262e]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default RuntimeHistory;